import React from 'react';
import { InputLabel, FormControl, Select, MenuItem } from '@material-ui/core';
import ExternalTemplate from '../../components/Templates/ExternalTemplate';
import Text from '../../components/Typography/Text';
import BackButton from '../../components/Buttons/BackButton';
import FormMessage from '../../components/Messages/FormMessage';
import TextField from '../../components/Textfields/TextField';
import PasswordField from '../../components/Textfields/PasswordField';
import CodeField from '../../components/Textfields/CodeField';
import Button from '../../components/Buttons/Button';
import { notifyInfo } from '../../components/Messages/Notification';
import { Grid } from '@material-ui/core';

import {
  forgotPassword,
  forgotPasswordCheckEmail,
} from '../../services/AdministratorService';
import { validateEmail, validatePassword } from '../../lib/validate/validate';
import { emailAuthCode } from '../../services/AuthService';

const successMessage = 'Password was updated.';

const ForgotPassword = ({ history }) => {
  const [isEmailValidated, setIsEmailValidated] = React.useState(false);
  const [securityQuestion, setSecurityQuestion] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [correspondent, setCorrespondent] = React.useState('');
  const [message, setMessage] = React.useState({
    type: '',
    value: '',
  });

  return (
    <ExternalTemplate>
      <form autoComplete="off">
        <Grid container>
          <Grid item xs={10}>
            <Text mb={40} variant="h2" label="Forgot Password" />
          </Grid>
          <Grid item xs={2}>
            <BackButton
              onClick={() => {
                history.push('/login');
              }}
            />
          </Grid>
          {message.value && (
            <Grid item xs={12}>
              <FormMessage type={message.type} message={message.value} />
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2}>
          <EmailForm
            email={email}
            setEmail={setEmail}
            correspondent={correspondent}
            setCorrespondent={setCorrespondent}
            setMessage={setMessage}
            setIsEmailValidated={setIsEmailValidated}
            setSecurityQuestion={setSecurityQuestion}
            isEmailValidated={isEmailValidated}
          />

          {isEmailValidated && message.value !== successMessage && (
            <EditForm
              email={email}
              correspondent={correspondent}
              securityQuestion={securityQuestion}
              setMessage={setMessage}
            />
          )}
        </Grid>
      </form>
    </ExternalTemplate>
  );
};

const EmailForm = ({
  email,
  setEmail,
  correspondent,
  setCorrespondent,
  setMessage,
  setIsEmailValidated,
  setSecurityQuestion,
  isEmailValidated,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [correspondents, setCorrespondents] = React.useState([]);

  const handleValidateEmail = async () => {
    setLoading(true);
    setMessage({});
    try {
      const { securityQuestion, correspondentsList } = await forgotPasswordCheckEmail(
        email,
        correspondent
      );

      if (correspondentsList?.length > 1) {
        setMessage({
          type: 'success',
          value:
            'The email you entered have multiple accounts. Please select a correspondent.',
        });
        setCorrespondents(correspondentsList);
        setLoading(false);
      } else {
        setCorrespondents([]);
        setCorrespondent(correspondentsList[0]);
        setIsEmailValidated(true);
        setSecurityQuestion(securityQuestion);
      }
    } catch (error) {
      setCorrespondents([]);
      setMessage({ value: error.message });
    }
    setLoading(false);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          autoFocus={true}
          max={50}
          name="email"
          label="Email Address"
          placeholder="youremail@website.com"
          type="text"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          showIcon
          disabled={isEmailValidated}
          completed={isEmailValidated}
        />
      </Grid>
      {correspondents?.length > 1 && (
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel shrink id="correspondent">
              Correspondent
            </InputLabel>
            <Select
              name="correspondent"
              fullWidth
              value={correspondent}
              onChange={(e) => {
                setCorrespondent(e.target.value);
              }}
            >
              {correspondents.map((correspondent) => {
                return (
                  <MenuItem key={correspondent} value={correspondent}>
                    {correspondent}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      )}
      {!isEmailValidated && (
        <Grid item xs={12}>
          <Button
            disabled={!email || loading || validateEmail(email)}
            loading={loading}
            label={loading ? 'Validating...' : 'Validate Email'}
            onClick={handleValidateEmail}
          />
        </Grid>
      )}
    </Grid>
  );
};

const EditForm = ({ email, correspondent, securityQuestion, setMessage }) => {
  const [loading, setLoading] = React.useState(false);
  const [externalAuthId, setExternalAuthId] = React.useState('');
  const [isCodeNotExpired, setIsCodeNotExpired] = React.useState({ timer: false });
  const [isEmailCodeSent, setIsEmailCodeSent] = React.useState(false);
  const [emailCodeMinutes, setEmailCodeMinutes] = React.useState(0);

  const [data, setData] = React.useState({
    email: email,
    correspondent: correspondent,
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;
    setData({
      ...data,
      [input.name]: input.value,
    });
  };

  const validate = () => {
    setMessage({ value: '' });

    if (!data.newPassword) {
      setMessage({ type: 'error', value: 'New Password is required.' });
      return;
    }
    if (!data.confirmPassword) {
      setMessage({ type: 'error', value: 'Confirm Password is required.' });
      return;
    }
    if (validatePassword(data.newPassword).err) {
      setMessage({
        type: 'error',
        value: 'New Password does not meet the requirement.',
      });
      return;
    }
    if (data.confirmPassword !== data.newPassword) {
      setMessage({
        type: 'error',
        value: 'Passwords does not match',
      });
      return;
    }
    if (data.newPassword !== data.confirmPassword) {
      setMessage({ type: 'error', value: 'Password does not match.' });
      return;
    }
    if (securityQuestion !== '' && !data.securityAnswer) {
      setMessage({ type: 'error', value: 'Security question answer is required.' });
      return;
    }

    return true;
  };

  const handleEmailCode = async () => {
    const isValidated = validate();
    if (!isValidated) {
      return;
    }

    setIsEmailCodeSent(false);
    setEmailCodeMinutes(0);
    setLoading(true);

    try {
      const { externalAuthId } = await emailAuthCode(data.email);
      setExternalAuthId(externalAuthId);
      setIsCodeNotExpired({ timer: true });
      setEmailCodeMinutes(2);
      setIsEmailCodeSent(true);
      setMessage({ type: 'success', value: 'Auth code was sent to your email.' });
    } catch (error) {
      setMessage({ value: error.message });
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    const isValidated = validate();
    if (!isValidated) {
      return;
    }

    setLoading(true);
    try {
      await forgotPassword({
        externalAuthId,
        ...data,
      });
      setMessage({ type: 'success', value: successMessage });
    } catch (error) {
      setMessage({ value: error.message });
      setLoading(false);
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <PasswordField
          max={20}
          showIcon={false}
          showPasswordRequirement={true}
          name="newPassword"
          label="New Password"
          placeholder="New Password"
          type="password"
          value={data.newPassword}
          onChange={handleChange}
        />
      </Grid>
      <Grid item xs={12}>
        <PasswordField
          max={20}
          showIcon={false}
          showPasswordRequirement={true}
          name="confirmPassword"
          label="Confirm Password"
          placeholder="Confirm Password"
          type="password"
          value={data.confirmPassword}
          onChange={handleChange}
        />
      </Grid>
      {securityQuestion && (
        <>
          <Grid item xs={12}>
            <TextField
              autoFocus={true}
              max={50}
              showIcon={false}
              name="securityAnswer"
              label={securityQuestion}
              placeholder="Answer to your security question"
              type="text"
              value={data.securityAnswer}
              onChange={handleChange}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12}>
        <CodeField
          mt={true}
          max={36}
          minutes={emailCodeMinutes}
          showIcon={false}
          startTimer={isEmailCodeSent}
          setLoading={setIsCodeNotExpired}
          name="code"
          label="Authentication Code"
          placeholder="36 Alphanumeric Code"
          type="text"
          value={data.code}
          onChange={handleChange}
          disabled={!isEmailCodeSent}
        />
      </Grid>
      <Grid item xs={6}>
        <Button
          disabled={loading}
          loading={loading}
          label={loading ? 'Sending...' : 'Email Code'}
          onClick={handleEmailCode}
        />
      </Grid>
      <Grid item xs={6}>
        <Button
          disabled={loading || !data.code || !isCodeNotExpired?.timer}
          loading={loading}
          label={loading ? 'Submiting...' : 'Submit'}
          onClick={handleSubmit}
        />
      </Grid>
    </>
  );
};

export default ForgotPassword;
